import { useCallback, useState } from 'react';
import { Routes, formatEvidenceForAgent, type FailureBundle, type FailureBundleResponse } from '@verifyloop/core';
import { ApiError, describeError, downloadFile } from '../api/client';
import { testFailure } from '../api/endpoints';
import { useResource, useToast } from '../state';
import { formatBytes, formatTimestamp } from '../lib/format';
import { ArtifactPreview } from './Artifact';
import { CopyButton } from './CopyButton';
import { ErrorNotice, InlineNotice, LoadingRows } from './Feedback';
import { Duration, FailureKindPill, Mono, StepStatusPill } from './Pills';
import { StepResultsView } from './StepViews';

/**
 * The bundle is the product.
 *
 * Everything a coding agent needs to fix its own work is in here and all of it
 * was captured under one `snapshotId`: what failed, its neighbours, a root
 * cause, a suggested fix with the target it applies to, and the evidence. It is
 * rendered to be read first and copied second: an agent should be able to take
 * the JSON as-is without the UI in the way.
 */
export function FailureBundleView({
  testId,
  runId,
}: {
  testId: string;
  runId?: string | undefined;
}): JSX.Element {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);

  const failure = useResource<FailureBundleResponse>(() => testFailure(testId, runId), [
    testId,
    runId ?? '',
  ]);

  const onDownload = useCallback(async () => {
    const id = failure.data?.bundle.runId;
    if (!id) return;
    setDownloading(true);
    try {
      await downloadFile(Routes.runArchive(id), `verifyloop-${id}.zip`);
      toast.success('Bundle downloaded', 'run.json, failure-bundle.json, plan, source and media.');
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setDownloading(false);
    }
  }, [failure.data, toast]);

  if (failure.loading) {
    return (
      <section className="panel panel--bundle">
        <LoadingRows rows={2} label="Loading failure bundle" />
      </section>
    );
  }

  if (failure.error) {
    const notFound = failure.error instanceof ApiError && failure.error.status === 404;
    if (notFound) {
      return (
        <InlineNotice tone="info">
          <strong>No failure bundle for this run.</strong> A bundle is written only when a run fails. This
          one did not, so there is nothing for an agent to fix.
        </InlineNotice>
      );
    }
    return (
      <section className="panel panel--bundle">
        <ErrorNotice error={failure.error} onRetry={failure.reload} title="Could not load the failure bundle" />
      </section>
    );
  }

  if (!failure.data) {
    return (
      <InlineNotice tone="info">
        No failure bundle has been captured for this test yet.
      </InlineNotice>
    );
  }

  const { bundle, artifactUrls } = failure.data;

  return (
    <section className="panel panel--bundle" aria-label="Failure bundle">
      <header className="panel__header">
        <div className="panel__title">
          <FailureKindPill kind={bundle.kind} />
          <h3>Failure bundle</h3>
          <span className="muted mono">{bundle.snapshotId}</span>
        </div>
        <div className="panel__actions">
          <CopyButton
            value={formatEvidenceForAgent(bundle, { artifactUrls })}
            label="Copy for Agent"
            title="Formatted prompt markdown for coding agents (< 1500 tokens)"
          />
          <CopyButton
            value={JSON.stringify(bundle, null, 2)}
            label="Copy JSON"
            title="Everything below, as raw JSON"
          />
          <button
            type="button"
            className="btn btn--small"
            onClick={() => void onDownload()}
            disabled={downloading}
          >
            {downloading ? 'Preparing…' : 'Download bundle (.zip)'}
          </button>
        </div>
      </header>

      <div className="bundle">
        <div className="bundle__grid">
          <BundleFact label="captured" value={formatTimestamp(bundle.capturedAt)} />
          <BundleFact label="failing step" value={`#${bundle.failingStepIndex + 1}`} />
          <BundleFact label="run" value={bundle.runId} mono />
          <BundleFact label="code version" value={bundle.codeVersion} mono />
        </div>

        <article className="bundle__statement">
          <h4>Root cause</h4>
          <p className="bundle__rootcause">{bundle.rootCause}</p>
          <p className="muted bundle__hint">
            All artifacts below were captured in the same snapshot. Never mix evidence from two runs.
          </p>
        </article>

        <article className="bundle__statement">
          <h4>Suggested fix</h4>
          <p className="bundle__fix">{bundle.suggestedFix}</p>
          {bundle.suggestedFixTarget ? (
            <p className="bundle__target">
              <span className="bundle__target-label">applies to</span>
              <Mono>{bundle.suggestedFixTarget}</Mono>
            </p>
          ) : null}
        </article>

        <section className="bundle__steps">
          <h4>Failing step</h4>
          <StepResultsView steps={[bundle.failingStep]} />

          {bundle.neighborSteps.length > 0 ? (
            <>
              <h4>Neighbouring steps</h4>
              <StepResultsView steps={bundle.neighborSteps} />
            </>
          ) : null}
        </section>

        <BundleScreenshots bundle={bundle} artifactUrls={artifactUrls} />

        {bundle.consoleErrors.length > 0 || bundle.networkErrors.length > 0 ? (
          <section className="bundle__logs">
            <h4>Observed during the run</h4>
            {bundle.consoleErrors.length > 0 ? (
              <>
                <h5>console</h5>
                <pre className="log">{bundle.consoleErrors.join('\n')}</pre>
              </>
            ) : null}
            {bundle.networkErrors.length > 0 ? (
              <>
                <h5>network</h5>
                <pre className="log">{bundle.networkErrors.join('\n')}</pre>
              </>
            ) : null}
          </section>
        ) : null}

        {bundle.source ? (
          <details className="bundle__source">
            <summary>Playwright source at the moment of failure</summary>
            <pre className="log">{bundle.source}</pre>
          </details>
        ) : null}
      </div>
    </section>
  );
}

function BundleFact({ label, value, mono = false }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="bundle__fact">
      <span className="bundle__fact-label">{label}</span>
      <span className={mono ? 'mono' : undefined} title={value}>
        {value}
      </span>
    </div>
  );
}

function BundleScreenshots({
  bundle,
  artifactUrls,
}: {
  bundle: FailureBundle;
  artifactUrls: Record<string, string>;
}): JSX.Element | null {
  const artifacts = bundle.failingStep.artifacts.filter(
    (artifact) => artifact.kind === 'screenshot' || artifact.kind === 'video',
  );
  if (artifacts.length === 0) return null;
  return (
    <section className="bundle__evidence">
      <h4>Evidence</h4>
      <div className="artifacts__grid">
        {artifacts.map((artifact) => (
          <ArtifactPreview
            key={artifact.id}
            runId={bundle.runId}
            artifact={artifact}
            {...(artifactUrls[artifact.id] ? { artifactUrl: artifactUrls[artifact.id] } : {})}
          />
        ))}
      </div>
    </section>
  );
}

/** Compact summary used in the run detail header. */
export function FailureSummaryLine({ bundle }: { bundle: FailureBundle }): JSX.Element {
  return (
    <p className="bundle__summary">
      <FailureKindPill kind={bundle.kind} />
      <span>
        step {bundle.failingStepIndex + 1} of the run failed after{' '}
        <Duration ms={bundle.failingStep.durationMs} />
      </span>
      <StepStatusPill status={bundle.failingStep.status} />
      <span className="muted">{formatBytes(bundle.failingStep.artifacts.reduce((sum, a) => sum + a.byteSize, 0))} of evidence</span>
    </p>
  );
}